import { Box, Button, IconButton } from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import ClearIcon from "@material-ui/icons/Clear";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import React, { Fragment, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useFilePicker } from "use-file-picker";
import { Input } from "components/shared/input";
import * as yup from "yup";

const urlSchema = yup.string().url().nullable().optional();

export interface RegularFileImportProps {
  onFileChanged: (file: File) => void;
  onUrlChanged?: (url: string | null) => void;
}

export const RegularFileImport = ({
  onFileChanged,
  onUrlChanged,
}: RegularFileImportProps) => {
  const [url, setUrl] = useState<string>("");
  const [urlError, setUrlError] = useState<string>("");
  const [openFileSelector, { plainFiles, clear, errors, filesContent }] =
    useFilePicker({
      multiple: false,
      accept: ["image/*"],
      limitFilesConfig: { max: 1 },
      maxFileSize: 5,
      imageSizeRestrictions: {
        minHeight: 240,
        minWidth: 240,
        maxWidth: 2048,
        maxHeight: 2048,
      },
      readAs: "DataURL",
    });

  const handleUrlChange = async (value: string) => {
    setUrl(value);
    if (value.trim() === "") {
      setUrlError("");
      onUrlChanged?.(null);
      return;
    }

    try {
      await urlSchema.validate(value);
      setUrlError("");
      onUrlChanged?.(value);
    } catch (error: any) {
      setUrlError(error.message || "Please enter a valid URL");
      onUrlChanged?.(null);
    }
  };

  useEffect(() => {
    if (errors.length) {
      if (errors[0].imageHeightTooSmall)
        toast.error("Image height is too small");
      if (errors[0].imageWidthTooSmall) toast.error("Image width is too small");
      if (errors[0].imageHeightTooBig) toast.error("Image height is too high");
      if (errors[0].imageWidthTooBig) toast.error("Image width is too high");

      if (errors[0].fileSizeToolarge)
        toast.error("The attachment size exceeds the allowable limit");

      clear();
    } else {
      if (/image/i.test(plainFiles[0]?.type) && filesContent[0]) {
        onFileChanged(plainFiles[0]);
      } else if (filesContent[0]) {
        toast.error("Please select an Image file");
        clear();
      }
    }
  }, [plainFiles, onFileChanged, errors[0]]);

  return (
    <>
      {plainFiles.length === 0 && (
        <Fragment>
          <Box
            sx={{ paddingBottom: 10, fontStyle: "italic" }}
            style={{ opacity: 0.7 }}
          >
            The user must upload the image with a minimum and maximum width and
            height of 240 pixels and 2048 pixels, respectively, and maximum file
            size of 5MB.
          </Box>
          <Button
            onClick={openFileSelector}
            size="large"
            style={{
              width: "100%",
              height: "20em",
              border: "2px dashed #C4C4C4",
            }}
          >
            <AttachFileIcon /> Please select a file
          </Button>
        </Fragment>
      )}
      {plainFiles.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "2em 0",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <InsertDriveFileIcon className="mr-1" fontSize="large" />
            <b>{plainFiles[0].name}</b>
          </Box>
          <IconButton onClick={clear}>
            <ClearIcon color="error" />
          </IconButton>
        </Box>
      )}
      <Box sx={{ marginTop: 2 }}>
        <Input
          className="mb-3"
        label="Optional: Redirect URL (opens on click)"
          onChange={(e: any) => handleUrlChange(e.currentTarget.value)}
          value={url}
          error={!!urlError}
          helperText={urlError}
          fullWidth
          placeholder="https://example.com"
        />
      </Box>
    </>
  );
};
